import { authClient } from "@/lib/auth-client";
import { safeNextPath } from "@/lib/safe-redirect";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

const searchSchema = z.object({
	next: z.string().optional(),
});

export const Route = createFileRoute("/two-factor")({
	component: TwoFactorPage,
	validateSearch: searchSchema,
});

type Mode = "totp" | "email" | "backup";

function TwoFactorPage() {
	const { next } = Route.useSearch();
	const [mode, setMode] = useState<Mode>("totp");

	function handleSuccess() {
		window.location.href = safeNextPath(next);
	}

	return (
		<div className="flex min-h-screen items-center justify-center bg-[#f8f9fb]">
			<div className="w-full max-w-sm space-y-6 rounded-lg border border-[#e5e7eb] bg-white p-8 shadow-sm">
				<div className="text-center">
					<div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-[#16a34a]">
						<span className="text-lg font-bold text-white">P</span>
					</div>
					<h1 className="text-xl font-semibold text-[#1e1e20]">Two-factor check</h1>
					<p className="mt-1 text-sm text-[#6b7280]">
						{mode === "totp" && "Enter the code from your authenticator app."}
						{mode === "email" && "We sent a 6-digit code to your email."}
						{mode === "backup" && "Use one of your backup codes."}
					</p>
				</div>

				{mode === "totp" && <TotpForm onSuccess={handleSuccess} />}
				{mode === "email" && <EmailOtpForm onSuccess={handleSuccess} />}
				{mode === "backup" && <BackupCodeForm onSuccess={handleSuccess} />}

				<div className="border-t border-[#e5e7eb] pt-4 text-center">
					<p className="text-xs text-[#6b7280]">Can't access your authenticator?</p>
					<div className="mt-2 flex items-center justify-center gap-3 text-xs">
						{mode !== "email" && (
							<button
								type="button"
								onClick={() => setMode("email")}
								className="font-medium text-[#4f46e5] hover:text-[#3730a3]"
							>
								Email me a code
							</button>
						)}
						{mode !== "backup" && (
							<>
								{mode !== "email" && <span className="text-[#d1d5db]">·</span>}
								<button
									type="button"
									onClick={() => setMode("backup")}
									className="font-medium text-[#4f46e5] hover:text-[#3730a3]"
								>
									Use a backup code
								</button>
							</>
						)}
						{mode !== "totp" && (
							<>
								<span className="text-[#d1d5db]">·</span>
								<button
									type="button"
									onClick={() => setMode("totp")}
									className="font-medium text-[#4f46e5] hover:text-[#3730a3]"
								>
									Use authenticator
								</button>
							</>
						)}
					</div>
					<Link
						to="/login"
						className="mt-4 inline-block text-xs text-[#6b7280] hover:text-[#1e1e20]"
					>
						Back to sign in
					</Link>
				</div>
			</div>
		</div>
	);
}

type FormProps = { onSuccess: () => void };

function TotpForm({ onSuccess }: FormProps) {
	const [code, setCode] = useState("");
	const [trustDevice, setTrustDevice] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(null);
		setLoading(true);
		try {
			const result = await authClient.twoFactor.verifyTotp({ code, trustDevice });
			if (result.error) {
				setError(result.error.message ?? "Invalid code");
				return;
			}
			onSuccess();
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Invalid code");
		} finally {
			setLoading(false);
		}
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			{error && <div className="rounded bg-[#fef2f2] p-3 text-sm text-[#dc2626]">{error}</div>}
			<CodeInput id="totp-code" value={code} onChange={setCode} length={6} placeholder="000000" />
			<TrustDeviceCheckbox checked={trustDevice} onChange={setTrustDevice} />
			<button
				type="submit"
				disabled={loading || code.length !== 6}
				className="h-9 w-full rounded bg-[#16a34a] text-sm font-semibold text-white hover:bg-[#15803d] disabled:opacity-50"
			>
				{loading ? "Verifying…" : "Verify"}
			</button>
		</form>
	);
}

function EmailOtpForm({ onSuccess }: FormProps) {
	const [code, setCode] = useState("");
	const [trustDevice, setTrustDevice] = useState(false);
	const [loading, setLoading] = useState(false);
	const [sending, setSending] = useState(false);
	const [sentOnce, setSentOnce] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function sendCode() {
		setSending(true);
		setError(null);
		try {
			const result = await authClient.twoFactor.sendOtp();
			if (result.error) {
				setError(result.error.message ?? "Failed to send code");
				return;
			}
			setSentOnce(true);
			toast.success("Code sent. Check your email.");
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Failed to send code");
		} finally {
			setSending(false);
		}
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(null);
		setLoading(true);
		try {
			const result = await authClient.twoFactor.verifyOtp({ code, trustDevice });
			if (result.error) {
				setError(result.error.message ?? "Invalid code");
				return;
			}
			onSuccess();
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Invalid code");
		} finally {
			setLoading(false);
		}
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			{error && <div className="rounded bg-[#fef2f2] p-3 text-sm text-[#dc2626]">{error}</div>}

			{!sentOnce && (
				<button
					type="button"
					onClick={sendCode}
					disabled={sending}
					className="h-9 w-full rounded bg-[#4f46e5] text-sm font-semibold text-white hover:bg-[#4338ca] disabled:opacity-50"
				>
					{sending ? "Sending…" : "Send code to my email"}
				</button>
			)}

			{sentOnce && (
				<>
					<CodeInput
						id="email-code"
						value={code}
						onChange={setCode}
						length={6}
						placeholder="000000"
					/>
					<TrustDeviceCheckbox checked={trustDevice} onChange={setTrustDevice} />
					<button
						type="submit"
						disabled={loading || code.length !== 6}
						className="h-9 w-full rounded bg-[#16a34a] text-sm font-semibold text-white hover:bg-[#15803d] disabled:opacity-50"
					>
						{loading ? "Verifying…" : "Verify"}
					</button>
					<button
						type="button"
						onClick={sendCode}
						disabled={sending}
						className="block w-full text-center text-xs text-[#4f46e5] hover:text-[#3730a3] disabled:opacity-50"
					>
						{sending ? "Sending…" : "Resend code"}
					</button>
				</>
			)}
		</form>
	);
}

function BackupCodeForm({ onSuccess }: FormProps) {
	const [code, setCode] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(null);
		setLoading(true);
		try {
			const result = await authClient.twoFactor.verifyBackupCode({ code });
			if (result.error) {
				setError(result.error.message ?? "Invalid backup code");
				return;
			}
			onSuccess();
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Invalid backup code");
		} finally {
			setLoading(false);
		}
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			{error && <div className="rounded bg-[#fef2f2] p-3 text-sm text-[#dc2626]">{error}</div>}
			<div className="space-y-1">
				<label htmlFor="backup-code" className="text-xs font-medium text-[#1e1e20]">
					Backup code
				</label>
				<input
					id="backup-code"
					type="text"
					value={code}
					onChange={(e) => setCode(e.target.value.trim())}
					required
					autoComplete="one-time-code"
					className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 font-mono text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
					placeholder="e.g. a1b2c3d4e5"
				/>
			</div>
			<button
				type="submit"
				disabled={loading || code.length === 0}
				className="h-9 w-full rounded bg-[#16a34a] text-sm font-semibold text-white hover:bg-[#15803d] disabled:opacity-50"
			>
				{loading ? "Verifying…" : "Verify backup code"}
			</button>
			<p className="text-xs text-[#6b7280]">
				Backup codes can only be used once. Regenerate them from your security settings after
				signing in.
			</p>
		</form>
	);
}

function CodeInput({
	id,
	value,
	onChange,
	length,
	placeholder,
}: {
	id: string;
	value: string;
	onChange: (v: string) => void;
	length: number;
	placeholder?: string;
}) {
	return (
		<input
			id={id}
			type="text"
			inputMode="numeric"
			pattern={`[0-9]{${length}}`}
			maxLength={length}
			value={value}
			onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
			required
			autoComplete="one-time-code"
			placeholder={placeholder}
			className="h-11 w-full rounded border border-[#d1d5db] bg-white px-3 text-center font-mono text-xl tracking-[0.3em] text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
		/>
	);
}

function TrustDeviceCheckbox({
	checked,
	onChange,
}: {
	checked: boolean;
	onChange: (v: boolean) => void;
}) {
	return (
		<label className="flex items-center gap-2 text-xs text-[#6b7280]">
			<input
				type="checkbox"
				checked={checked}
				onChange={(e) => onChange(e.target.checked)}
				className="h-4 w-4 rounded border-[#d1d5db] text-[#16a34a] focus:ring-[#4f46e5]"
			/>
			Trust this device for 30 days
		</label>
	);
}
